// This component is essentially just styles with no functionality, so safe to
// ignore for coverage
/* istanbul ignore file */

import { corners, media } from "../../styles";
import styled, { css } from "styled-components";
import { applyOpacity } from "../../styles/utils";
import React from "react";
export type CheckboxProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, "type">;

/**
 * Builds the checkmark icon data-uri with a given fill colour. Previously
 * this was two fixed variants (`whiteCheck`/`blackCheck`, hardcoded
 * `#ffffff`/`#1a1a1a`) always paired with a hardcoded dark ink checked
 * background — on a dark brand theme, that background is near-black and a
 * near-black checkmark on top of it (via the focus-only `blackCheck`
 * override) is invisible, and even the default `whiteCheck` on a
 * plain-ink-coloured background isn't guaranteed to contrast with the
 * brand's actual colours. Building the icon from `--d-on-action` (already
 * computed in theme.ts for exactly this purpose — best-contrast text
 * against `--d-primary`) keeps the checkmark visible against whatever
 * brand primary colour fills the box.
 */
const checkIcon = (fill: string) => css`
  content: url("data:image/svg+xml; utf8, <svg width='16' height='12' viewBox='0 0 16 12' xmlns='http://www.w3.org/2000/svg'><g id='check'><path id='Path' d='M5.59466 11.7189L0.719661 6.84392C0.42678 6.55104 0.42678 6.07616 0.719661 5.78325L1.7803 4.72259C2.07318 4.42968 2.54808 4.42968 2.84096 4.72259L6.12499 8.00659L13.159 0.97259C13.4519 0.679709 13.9268 0.679709 14.2197 0.97259L15.2803 2.03325C15.5732 2.32614 15.5732 2.80101 15.2803 3.09392L6.65532 11.7189C6.36241 12.0118 5.88754 12.0118 5.59466 11.7189V11.7189Z' fill='${fill.replace("#", "%23")}'/></g></svg>");
`;
const Input = styled.input.attrs({
  type: "checkbox"
})`
  // Disable default checkbox
  appearance: none;
  -webkit-appearance: none;
  position: relative;

  margin: 0px;

  height: 1.5rem;
  width: 1.5rem;
  flex-shrink: 0;

  border: 2px solid ${({
  theme
}) => theme.brand.cssVars["--d-border"]};
  ${corners.m}

  &:checked {
    background: ${({
  theme
}) => theme.brand.cssVars["--d-primary"]};
    border-color: ${({
  theme
}) => theme.brand.cssVars["--d-primary"]};
    ::after {
      ${({
  theme
}) => checkIcon(theme.brand.cssVars["--d-on-action"])}
      position: absolute;
      left: 0.1rem;
      top: -0.2rem;
    }
  }

  @media ${media.hoverable} {
    &:hover {
      cursor: pointer;
    }
  }

  &:focus {
    outline: none;
    background: ${({
  theme
}) => applyOpacity(theme.brand.cssVars["--d-text"], 0.1, true, theme.brand.cssVars["--d-surface"])};
    /*
     * Focusing a checked box overrides the solid --d-primary background
     * above with a lighter tinted wash (so the focus state itself is
     * still visible), so the checkmark also needs to switch to an
     * ink-toned fill (rather than --d-on-action, which is only guaranteed
     * to contrast against --d-primary) to stay visible against that wash.
     */
    &:checked::after {
      ${({
  theme
}) => checkIcon(theme.brand.cssVars["--d-text"])}
    }
  }
`;
export function Checkbox(props: CheckboxProps) {
  return <Input {...props} data-sentry-element="Input" data-sentry-component="Checkbox" data-sentry-source-file="Checkbox.tsx" />;
}
export default Checkbox;